import { useCallback, useEffect, useRef, useState } from "react";

export type BroadcastChannelData =
  | string
  | number
  | boolean
  | Record<string, unknown>
  | undefined
  | null;

export function useBroadcastChannel<T extends BroadcastChannelData = string>(
  channelName: string,
  handleMessage?: (event: MessageEvent, channel: BroadcastChannel) => void,
  handleMessageError?: (event: MessageEvent, channel: BroadcastChannel) => void
): {
  postMessage: (data: T) => void;
  close: () => void;
} {
  const [channel] = useState<BroadcastChannel | null>(() =>
    typeof window !== "undefined" && "BroadcastChannel" in window
      ? new BroadcastChannel(channelName + "-channel")
      : null
  );

  useChannelEventListener(channel, "message", (event) => {
    if (handleMessage && channel) {
      handleMessage(event, channel);
    }
  });

  useChannelEventListener(channel, "messageerror", (event) => {
    if (handleMessageError && channel) {
      handleMessageError(event, channel);
    }
  });

  const postMessage = useCallback(
    (data: T) => channel?.postMessage(data),
    [channel]
  );

  const close = useCallback(() => {
    channel?.close();
  }, [channel]);

  return { postMessage, close };
}

function useChannelEventListener<T extends keyof BroadcastChannelEventMap>(
  channel: BroadcastChannel | null,
  event: T,
  handler?: (e: BroadcastChannelEventMap[T]) => void
) {
  const callbackRef = useRef(handler);
  if (callbackRef.current !== handler) {
    callbackRef.current = handler;
  }

  useEffect(() => {
    if (!channel) {
      return;
    }

    const callback = (e: BroadcastChannelEventMap[T]) =>
      callbackRef.current?.(e);

    channel.addEventListener(event, callback);
    return () => channel.removeEventListener(event, callback);
  }, [channel, event]);
}
